// Settings → VPS agents. Admin-only management of the per-VPS HMAC ingestion credentials.
//
// This is the only place in the (read-only) console that mints or changes a credential, so the
// whole card is hidden for non-admin roles and every mutation goes through the CSRF-protected,
// audit-logged client. Newly created / rotated secrets are shown EXACTLY ONCE — there is no way
// to retrieve them again — so the one-time panel makes that unmistakable and offers a ready-to-
// paste collector appsettings.json snippet.

import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../api/client';
import { ApiError } from '../api/http';
import type { AgentSummary } from '../types/api';
import { C, MONO } from '../lib/tokens';
import { PBTN, GBTN_SM, H2, badgeS, mTh, mTd } from '../lib/styles';
import { parseTs, fmtTS, fmtAge } from '../lib/format';
import { useAuth, useServerNow } from '../store/hooks';

type OneTime = { agent_id: string; secret: string; kind: 'created' | 'rotated' };
type Confirm = { id: string; action: 'revoke' | 'delete' } | null;

function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message || fallback;
  return fallback;
}

/** Ready-to-paste collector appsettings.json fragment for a freshly provisioned agent. */
function appsettingsSnippet(origin: string, agentId: string, secret: string): string {
  return JSON.stringify(
    { Collector: { ServerBaseUrl: origin, AgentId: agentId, AgentSecret: secret } },
    null,
    2,
  );
}

const INPUT: React.CSSProperties = {
  height: 32,
  flex: 1,
  minWidth: 160,
  background: '#0A0F19',
  border: '1px solid ' + C.ln,
  borderRadius: 6,
  color: C.tx,
  fontSize: 12.5,
  padding: '0 10px',
  fontFamily: 'inherit',
};

const CODE_BLOCK: React.CSSProperties = {
  fontFamily: MONO,
  fontSize: 11.5,
  color: C.tx,
  background: '#0A0F19',
  border: '1px solid ' + C.ln,
  borderRadius: 6,
  padding: '10px 12px',
  margin: 0,
  whiteSpace: 'pre',
  overflowX: 'auto',
  userSelect: 'all',
};

const DANGER_BTN: React.CSSProperties = {
  ...GBTN_SM,
  color: C.rd,
  border: '1px solid ' + C.rd + '77',
};

export function AgentsCard() {
  const role = useAuth((s) => s.user?.role);
  const serverNow = useServerNow();

  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null); // row id (or '*' for create) in flight
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [oneTime, setOneTime] = useState<OneTime | null>(null);
  const [copied, setCopied] = useState(false);
  const labelRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.listAgents();
      setAgents(res.agents);
      setError(null);
    } catch (e) {
      setError(errText(e, 'Could not load agents.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (role !== 'admin') return;
    void refresh();
  }, [role, refresh]);

  // Only admins see this section at all.
  if (role !== 'admin') return null;

  async function addAgent(): Promise<void> {
    const name = label.trim();
    if (!name || busyId) return;
    setBusyId('*');
    setError(null);
    setNotice(null);
    try {
      const res = await api.createAgent(name);
      setOneTime({ agent_id: res.agent_id, secret: res.secret, kind: 'created' });
      setCopied(false);
      setLabel('');
      await refresh();
    } catch (e) {
      setError(errText(e, 'Could not create the agent.'));
    } finally {
      setBusyId(null);
    }
  }

  async function rotate(id: string): Promise<void> {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      const res = await api.rotateAgent(id);
      setOneTime({ agent_id: res.agent_id, secret: res.secret, kind: 'rotated' });
      setCopied(false);
      await refresh();
    } catch (e) {
      setError(errText(e, 'Could not rotate the secret.'));
    } finally {
      setBusyId(null);
    }
  }

  async function doConfirm(): Promise<void> {
    if (!confirm || busyId) return;
    const { id, action } = confirm;
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      if (action === 'revoke') {
        await api.revokeAgent(id);
        setNotice(`Agent ${id} revoked. Its collector can no longer send telemetry.`);
      } else {
        await api.deleteAgent(id);
        setNotice(`Agent ${id} deleted.`);
        if (oneTime?.agent_id === id) setOneTime(null);
      }
      setConfirm(null);
      await refresh();
    } catch (e) {
      setError(errText(e, 'Action failed.'));
    } finally {
      setBusyId(null);
    }
  }

  async function copySnippet(): Promise<void> {
    if (!oneTime) return;
    const text = appsettingsSnippet(window.location.origin, oneTime.agent_id, oneTime.secret);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Could not copy to clipboard — select the snippet and copy manually.');
    }
  }

  return (
    <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={H2}>VPS agents</h2>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.dm, background: 'rgba(255,255,255,.04)', border: '1px solid ' + C.ln, borderRadius: 4, padding: '2px 8px' }}>
          Admin
        </span>
      </div>

      <div style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.5 }}>
        Each VPS collector authenticates to the ingestion API with its own HMAC secret. Provision one agent per VPS,
        rotate its secret periodically, and revoke it if a host is decommissioned or compromised. Secrets are shown once.
      </div>

      {notice && (
        <div role="status" style={{ fontSize: 11.5, color: C.gn, background: 'rgba(63,191,127,.1)', border: '1px solid rgba(63,191,127,.35)', borderRadius: 6, padding: '7px 10px' }}>
          {notice}
        </div>
      )}
      {error && (
        <div role="alert" style={{ fontSize: 11.5, color: C.rd, background: 'rgba(228,91,91,.1)', border: '1px solid rgba(228,91,91,.35)', borderRadius: 6, padding: '7px 10px', lineHeight: 1.5 }}>
          {error}
        </div>
      )}

      {/* Add-agent form */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          ref={labelRef}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void addAgent();
          }}
          placeholder="VPS label (e.g. London VPS 01)"
          maxLength={120}
          aria-label="New VPS label"
          style={INPUT}
        />
        <button
          onClick={addAgent}
          disabled={busyId === '*' || label.trim().length === 0}
          style={{ ...PBTN, opacity: busyId === '*' || label.trim().length === 0 ? 0.6 : 1, cursor: busyId === '*' || label.trim().length === 0 ? 'default' : 'pointer' }}
        >
          {busyId === '*' ? 'Creating…' : 'Add VPS agent'}
        </button>
      </div>

      {/* One-time secret panel */}
      {oneTime && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, border: '1px solid ' + C.am + '66', background: 'rgba(224,168,62,.07)', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.am }}>
              {oneTime.kind === 'created' ? 'Agent created' : 'Secret rotated'}
            </span>
            <span style={badgeS(C.am)}>shown once</span>
            <button onClick={() => setOneTime(null)} style={{ ...GBTN_SM, marginLeft: 'auto' }}>Dismiss</button>
          </div>
          <div style={{ fontSize: 11.5, color: C.am, lineHeight: 1.5 }}>
            Copy this secret now — it is never shown again. Store it in the VPS collector configuration, then keep it out of source control.
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 10, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Agent ID</span>
              <code style={{ fontFamily: MONO, fontSize: 12.5, color: C.tx, userSelect: 'all' }}>{oneTime.agent_id}</code>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 260, flex: 1 }}>
              <span style={{ fontSize: 10, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Agent secret</span>
              <code style={{ fontFamily: MONO, fontSize: 12.5, color: C.tx, wordBreak: 'break-all', userSelect: 'all' }}>{oneTime.secret}</code>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>collector appsettings.json</span>
            <button onClick={copySnippet} style={{ ...GBTN_SM, marginLeft: 'auto' }}>{copied ? 'Copied ✓' : 'Copy'}</button>
          </div>
          <pre style={CODE_BLOCK}>{appsettingsSnippet(window.location.origin, oneTime.agent_id, oneTime.secret)}</pre>
          <div style={{ fontSize: 10.5, color: C.dim2, lineHeight: 1.5 }}>
            For a hardened Windows deployment, run <code style={{ fontFamily: MONO }}>Watchtower.Collector protect-secret</code> and place the
            protected blob in <code style={{ fontFamily: MONO }}>AgentSecretProtected</code> instead of a plaintext <code style={{ fontFamily: MONO }}>AgentSecret</code>.
          </div>
        </div>
      )}

      {/* Agents table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead>
            <tr>
              <th style={mTh('left')}>Label</th>
              <th style={mTh('left')}>Agent ID</th>
              <th style={mTh('left')}>Status</th>
              <th style={mTh('left')}>Last seen</th>
              <th style={mTh('left')}>Created</th>
              <th style={mTh('right')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ ...mTd('left'), color: C.dm }}>Loading…</td></tr>
            )}
            {!loading && agents.length === 0 && (
              <tr><td colSpan={6} style={{ ...mTd('left'), color: C.dm }}>No agents yet. Add one above to onboard a VPS.</td></tr>
            )}
            {agents.map((a) => {
              const lastMs = parseTs(a.last_seen_at);
              const rowBusy = busyId === a.agent_id;
              const isConfirming = confirm?.id === a.agent_id;
              return (
                <tr key={a.agent_id}>
                  <td style={mTd('left')}>{a.vps_label ?? '—'}</td>
                  <td style={{ ...mTd('left'), ...{ fontFamily: MONO, color: C.b9 } }}>{a.agent_id}</td>
                  <td style={mTd('left')}>
                    <span style={badgeS(a.status === 'active' ? C.gn : C.rd)}>{a.status}</span>
                    {a.has_prev_secret && a.status === 'active' && (
                      <span title="A previous secret is still accepted during the rotation window" style={{ ...badgeS(C.bl), marginLeft: 6 }}>rotating</span>
                    )}
                  </td>
                  <td style={{ ...mTd('left'), color: C.dm }} title={lastMs ? fmtTS(lastMs) : ''}>
                    {lastMs ? fmtAge(serverNow - lastMs) + ' ago' : 'Never'}
                  </td>
                  <td style={{ ...mTd('left'), color: C.dm }}>{fmtTS(parseTs(a.created_at))}</td>
                  <td style={mTd('right')}>
                    {isConfirming ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: 10.5, color: C.rd }}>Confirm {confirm!.action}?</span>
                        <button onClick={doConfirm} disabled={rowBusy} style={{ ...DANGER_BTN, opacity: rowBusy ? 0.6 : 1 }}>
                          {rowBusy ? '…' : 'Yes'}
                        </button>
                        <button onClick={() => setConfirm(null)} disabled={rowBusy} style={GBTN_SM}>No</button>
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {a.status === 'active' && (
                          <button onClick={() => rotate(a.agent_id)} disabled={!!busyId} style={{ ...GBTN_SM, opacity: busyId ? 0.6 : 1 }}>
                            {rowBusy ? 'Rotating…' : 'Rotate'}
                          </button>
                        )}
                        {a.status === 'active' && (
                          <button onClick={() => { setConfirm({ id: a.agent_id, action: 'revoke' }); setError(null); setNotice(null); }} disabled={!!busyId} style={{ ...GBTN_SM, opacity: busyId ? 0.6 : 1 }}>
                            Revoke
                          </button>
                        )}
                        <button onClick={() => { setConfirm({ id: a.agent_id, action: 'delete' }); setError(null); setNotice(null); }} disabled={!!busyId} style={{ ...DANGER_BTN, opacity: busyId ? 0.6 : 1 }}>
                          Delete
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
